import React, { useEffect, useState } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Button,
  Form,
  Spinner,
  Alert,
} from "react-bootstrap";
import { httpsCallable } from "firebase/functions";
import { useAuth } from "../../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { functions } from "../../../firebase/config";
import RavenHead from "../../../assets/smoke-raven-avatar.webp";
import onomasticonTile from "../../../assets/tile-logo-onomasticon.webp";
import citizensGuideTeaser from "../../../assets/citizens-guide-teaser.webp";
import forbiddenLandsMapTile from "../../../assets/forbidden-lands-map-member-tile.webp";

// Members-only content, in display order.
const memberContent = [
  {
    href: "/blades-in-the-dark-doskvol-guide",
    image: citizensGuideTeaser,
    imageAlt: "A Citizen's Guide to the Supernatural",
    imageFit: "cover",
    system: "Blades in the Dark",
    title: "A Citizen's Guide to the Supernatural",
    description:
      "A full PDF guide to the ghosts, demons, and horrors haunting the streets of Doskvol.",
    cta: "Read the Guide",
    isNew: true,
  },
  {
    href: "/onomasticon",
    image: onomasticonTile,
    imageAlt: "Onomasticon - A Book of Names for Foundry VTT",
    imageFit: "contain",
    system: "Foundry VTT Module",
    title: "Onomasticon",
    description:
      "A name generator for Foundry VTT, with fantasy, sci-fi, modern, and homebrew name lists.",
    cta: "Get Onomasticon",
  },
  {
    href: "/forbidden-lands-campaign-maps",
    image: forbiddenLandsMapTile,
    imageAlt: "Forbidden Lands map with hex number overlay",
    imageFit: "cover",
    system: "Forbidden Lands",
    title: "Campaign Maps",
    description:
      "Super-high-resolution Ravenland and Bitter Reach maps with hex number overlays.",
    cta: "View the Maps",
  },
];

const Dashboard = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const [subscribed, setSubscribed] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [subscriptionUpdating, setSubscriptionUpdating] = useState(false);
  const [manualResubscribeNeeded, setManualResubscribeNeeded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadStatus = async () => {
      try {
        const getSubscriptionStatus = httpsCallable(
          functions,
          "getSubscriptionStatus",
        );
        const { data } = await getSubscriptionStatus();
        if (!cancelled) {
          // `null` means no sync has run for this account yet — treat as
          // subscribed, matching what the next sync will actually set.
          setSubscribed(data.subscribed === null ? true : data.subscribed);
        }
      } catch (error) {
        console.error("Failed to load subscription status:", error);
      } finally {
        if (!cancelled) setSubscriptionLoading(false);
      }
    };

    loadStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubscriptionToggle = async (e) => {
    const next = e.target.checked;
    setSubscriptionUpdating(true);
    setManualResubscribeNeeded(false);

    try {
      const updateSubscription = httpsCallable(functions, "updateSubscription");
      const { data } = await updateSubscription({ subscribed: next });
      setSubscribed(data.subscribed);
      if (data.mailchimpAction === "manual_required") {
        setManualResubscribeNeeded(true);
      }
    } catch (error) {
      console.error("Failed to update subscription:", error);
    } finally {
      setSubscriptionUpdating(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/");
    } catch (error) {
      console.error("Failed to log out:", error);
    }
  };

  return (
    <Container className="mt-5">
      <Row>
        <Col lg={10} xl={9} className="mx-auto">
          <Card className="mb-5">
            <Card.Header as="h5" className="bg-success text-white">
              Member Dashboard
            </Card.Header>
            <Card.Body>
              <div className="memberProfile">
                <img
                  src={currentUser?.photoURL || RavenHead}
                  alt="Profile"
                  className="rounded-circle memberProfileAvatar"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = RavenHead;
                  }}
                />
                <div className="memberProfileInfo">
                  <h4 className="mb-1">
                    Welcome, {currentUser?.displayName || "User"}!
                  </h4>
                  <div>
                    {currentUser?.email}{" "}
                    {currentUser?.emailVerified ? (
                      <span className="text-success">✓ Verified</span>
                    ) : (
                      <span className="text-warning">⚠ Not verified</span>
                    )}
                  </div>
                  {/* <li><strong>User ID:</strong> {currentUser?.uid}</li>
                  <li><strong>Provider:</strong> {currentUser?.providerData[0]?.providerId}</li> */}
                </div>
                <div className="memberProfileEmail">
                  <div className="memberProfileEmailLabel">Email Updates</div>
                  {subscriptionLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <Form.Check
                        type="switch"
                        id="subscription-toggle"
                        label={
                          subscribed
                            ? "Subscribed to updates"
                            : "Not subscribed"
                        }
                        checked={Boolean(subscribed)}
                        onChange={handleSubscriptionToggle}
                        disabled={subscriptionUpdating}
                      />
                      {manualResubscribeNeeded && (
                        <Alert variant="warning" className="mt-2 py-2">
                          Mailchimp requires you to confirm resubscribing
                          yourself for anti-spam compliance. Please check your
                          email for a confirmation link, or contact us if you
                          don't receive one.
                        </Alert>
                      )}
                    </>
                  )}
                </div>
              </div>

              <h5 className="memberSectionTitle">🔒 Members-Only Content</h5>
              <Row xs={1} md={3} className="g-4 mb-4">
                {memberContent.map((item) => (
                  <Col key={item.href}>
                    <a href={item.href} className="memberTile">
                      <div className="memberTileImageWrap">
                        <img
                          src={item.image}
                          alt={item.imageAlt}
                          className="memberTileImage"
                          style={{ objectFit: item.imageFit }}
                        />
                        {item.isNew && (
                          <span className="memberTileBadge">New</span>
                        )}
                      </div>
                      <div className="memberTileBody">
                        <div className="memberTileSystem">{item.system}</div>
                        <div className="memberTileTitle">{item.title}</div>
                        <p className="memberTileDescription">
                          {item.description}
                        </p>
                        <span className="memberTileCta">{item.cta} →</span>
                      </div>
                    </a>
                  </Col>
                ))}
              </Row>
              <p className="text-muted text-center small mb-4">
                More free, members-only gaming materials are on the way, and
                we'll email you when they arrive.
              </p>

              <div className="d-flex gap-2 justify-content-center">
                <Button variant="primary" onClick={() => navigate("/")}>
                  Go to Home
                </Button>
                <Button variant="outline-danger" onClick={handleLogout}>
                  Sign Out
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;
